import React from 'react';
import styled from '@emotion/styled';
import { css, SerializedStyles } from '@emotion/react';
import { aigleButtonStyles } from '@styles/buttonStyleGuide';

/**
 * AigleButton Props
 */
export interface AigleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** 버튼 variant 타입 */
  variant?: 'contained' | 'outlined' | 'transparent';
  /** 버튼 색상 타입 */
  color?: 'primary' | 'secondary' | 'default' | 'disabled';
  /** 버튼 크기 */
  size?: 'small' | 'medium' | 'large';
  /** 전체 너비 사용 여부 */
  fullWidth?: boolean;
  /** 아이콘 (왼쪽) */
  startIcon?: React.ReactNode;
  /** 아이콘 (오른쪽) */
  endIcon?: React.ReactNode;
  /** 아이콘만 표시 (텍스트 없음) */
  iconOnly?: boolean;
  /** 자식 요소 */
  children?: React.ReactNode;
  /** 추가 스타일 */
  customStyle?: SerializedStyles;
}

// Styled Button Component
const StyledButton = styled.button<{
  $variant: 'contained' | 'outlined' | 'transparent';
  $color: 'primary' | 'secondary' | 'default' | 'disabled';
  $size: 'small' | 'medium' | 'large';
  $fullWidth: boolean;
  $iconOnly: boolean;
  $customStyle?: SerializedStyles;
}>`
  ${({ $variant, $color, $size, $fullWidth, $iconOnly, $customStyle }) => {
    let buttonStyle;

    // variant와 color에 따른 기본 스타일 선택
    if ($iconOnly) {
      buttonStyle = aigleButtonStyles.iconOnly[$color === 'primary' ? 'primary' : 'default'];
    } else if ($variant === 'transparent') {
      buttonStyle = aigleButtonStyles.transparent;
    } else {
      buttonStyle = aigleButtonStyles[$variant][$color];
    }

    return css`
      ${buttonStyle}
      ${aigleButtonStyles.size[$size]}
      ${$fullWidth && aigleButtonStyles.fullWidth}
      ${aigleButtonStyles.icon}
      ${$customStyle}
    `;
  }}
`;

/**
 * AigleButton Component
 * 
 * 일관된 디자인의 버튼 컴포넌트
 * 
 * @example
 * // Primary Contained Button
 * <AigleButton variant="contained" color="primary">
 *   버튼
 * </AigleButton>
 * 
 * @example
 * // Outlined Button with Icon
 * <AigleButton variant="outlined" color="secondary" startIcon={<RefreshIcon />}>
 *   새로고침
 * </AigleButton>
 * 
 * @example
 * // Icon Only Button
 * <AigleButton iconOnly color="primary">
 *   <RefreshIcon />
 * </AigleButton>
 */
export const AigleButton = React.forwardRef<HTMLButtonElement, AigleButtonProps>(
  (
    {
      variant = 'contained',
      color = 'primary',
      size = 'medium',
      fullWidth = false,
      startIcon,
      endIcon,
      iconOnly = false,
      children,
      disabled = false,
      customStyle,
      ...props
    },
    ref
  ) => {
    // disabled 상태일 때 color를 'disabled'로 처리하여 disabled 스타일 적용
    const effectiveColor = disabled ? 'disabled' : color;

    return (
      <StyledButton
        ref={ref}
        $variant={variant}
        $color={effectiveColor}
        $size={size}
        $fullWidth={fullWidth}
        $iconOnly={iconOnly}
        $customStyle={customStyle}
        disabled={disabled}
        {...props}
      >
        {startIcon && <span className="button-icon-start">{startIcon}</span>}
        {children}
        {endIcon && <span className="button-icon-end">{endIcon}</span>}
      </StyledButton>
    );
  }
);

AigleButton.displayName = 'AigleButton';

export default AigleButton;

